import { useTranslation } from 'react-i18next';
import type { WeekDateRangeResponse } from '../../api/generated';
import type { UseYearPlanSimulationFormResult } from '../../hooks/useYearPlanSimulationForm';
import { MAX_STAFF_LOAD_CEILING_PERCENT } from '../../constants/yearPlanSimulationConstants';
import { formatDkk } from '../../utils/currencyUtils';
import { InputFieldNumber } from '../UI';
import { SimulationTemplateRow } from './SimulationTemplateRow';
import { SimulationWeekDropdown } from './SimulationWeekDropdown';
import styles from './YearPlanSimulationModal.module.css';

interface SimulationSettingsFormProps {
  form: UseYearPlanSimulationFormResult;
  weeks: WeekDateRangeResponse[];
  isResourcesLoaded: boolean;
  hasResourcesLoadError: boolean;
}

export const SimulationSettingsForm = ({
  form,
  weeks,
  isResourcesLoaded,
  hasResourcesLoadError
}: SimulationSettingsFormProps) => {
  const { t, i18n } = useTranslation();

  return (
    <div className={styles.body}>
      <p className={styles.intro}>{t('simulation-intro')}</p>

      <h3 className={styles.sectionTitle}>{t('simulation-period-title')}</h3>
      <div className={styles.fieldRow}>
        <SimulationWeekDropdown
          label={t('simulation-from-week-label')}
          weeks={weeks}
          weekNumber={form.fromWeekNumber}
          hasError={!form.isPeriodValid}
          onChange={form.setFromWeekNumber}
        />
        <SimulationWeekDropdown
          label={t('simulation-to-week-label')}
          weeks={weeks}
          weekNumber={form.toWeekNumber}
          hasError={!form.isPeriodValid}
          onChange={form.setToWeekNumber}
        />
        <InputFieldNumber
          label={t('simulation-max-staff-load-label')}
          value={form.maxStaffLoadPercent}
          suffixLabel="%"
          maxLimit={MAX_STAFF_LOAD_CEILING_PERCENT}
          error={!form.isMaxStaffLoadValid}
          onChange={(e) => form.setMaxStaffLoadPercent(e.target.value ? Number(e.target.value) : 0)}
        />
      </div>
      {!form.isPeriodValid && <p className={styles.error}>{t('simulation-period-error')}</p>}

      <h3 className={styles.sectionTitle}>{t('simulation-templates-title')}</h3>
      {form.templates.length === 0 ? (
        <p className={styles.hint}>{t('simulation-no-templates')}</p>
      ) : (
        <div className={styles.templateList}>
          {form.templates.map((template) => (
            <SimulationTemplateRow
              key={template.id}
              template={template}
              row={form.getRow(template.id)}
              onChange={(patch) => form.updateRow(template.id, patch)}
            />
          ))}
        </div>
      )}
      {form.hasIncompleteSelection && (
        <p className={styles.error}>{t('simulation-incomplete-selection-error')}</p>
      )}
      {form.totals.courseCount > 0 && (
        <p className={styles.totals}>
          {t('simulation-totals', {
            count: form.totals.courseCount,
            patients: form.totals.patientCount,
            revenue: formatDkk(form.totals.revenue, i18n.language)
          })}
        </p>
      )}
      {hasResourcesLoadError ? (
        <p className={styles.error}>{t('simulation-resources-load-error')}</p>
      ) : (
        !isResourcesLoaded && <p className={styles.hint}>{t('simulation-resources-loading')}</p>
      )}
    </div>
  );
};
